import { LINKS } from '@utils/constants';
import { constructGroupSlug, constructProductSlug } from '@utils/helpers';
import cx from 'classnames';
import { motion } from 'framer-motion';
import { Link } from 'gatsby';
import { StaticQuery, graphql } from 'gatsby';
import React, { useState } from 'react';
import { useFlexSearch } from 'react-use-flexsearch';

import * as styles from './ProductSearch.module.css';

import { default as SearchIcon } from '@components/SVG/Search';
import Copy from '@components/Typography/Copy';

const ProductSearch = ({
	type = 'standard',
	theme = 'standard',
	className = '',
}: {
	type?: 'standard' | 'alternate' | 'compact';
	theme?: 'standard' | 'dark';
	className?: string;
}) => {
	const isBrowser = typeof window !== 'undefined';

	if (!isBrowser) return null;

	return (
		<StaticQuery
			query={graphql`
				query {
					localSearchProducts {
						store
						index
					}
					categories: allPrismicProductCategory {
						edges {
							node {
								type
								data {
									title
									image {
										gatsbyImageData
									}
								}
								uid
							}
						}
					}
					groups: allPrismicProductGroup {
						edges {
							node {
								type
								data {
									title
									image {
										gatsbyImageData
									}
									categorisation {
										document {
											... on PrismicProductCategory {
												uid
												data {
													title
												}
												type
											}
										}
									}
								}
								uid
							}
						}
					}
					products: allPrismicProduct {
						edges {
							node {
								data {
									title
									summary {
										html
									}
									product_image {
										gatsbyImageData
									}
									categorisation {
										document {
											... on PrismicProductCategory {
												uid
												data {
													title
												}
												type
											}
											... on PrismicProductGroup {
												uid
												data {
													categorisation {
														document {
															... on PrismicProductCategory {
																data {
																	title
																}
																type
																uid
															}
														}
													}
													title
												}
												type
											}
										}
									}
								}
								uid
							}
						}
					}
				}
			`}
			render={(data) => {
				const { index, store } = data.localSearchProducts;

				const { search } = window.location;
				const query = new URLSearchParams(search).get('s');
				const [searchQuery, setSearchQuery] = useState(query || '');

				const results = useFlexSearch(searchQuery, index, store);

				const [focus, setFocus] = useState(false);

				const initialResults = [
					data.products.edges,
					data.groups.edges,
					data.categories.edges,
				].flat();

				const queriedResults = searchQuery ? results : initialResults;
				const groups = data.groups;
				const categories = data.categories;

				return (
					<motion.div
						className={styles.bounds}
						onFocus={() => setFocus(true)}
						onBlur={(e) => {
							const currentTarget = e.currentTarget;

							// Give browser time to focus the next element
							requestAnimationFrame(() => {
								// Check if the new focused element is a child of the original container
								if (
									!currentTarget.contains(
										document.activeElement
									)
								) {
									setFocus(false);
								}
							});
						}}
						initial="hidden"
						whileInView="visible"
						viewport={{ once: true, amount: 0.6 }}
						transition={{ duration: 0.35 }}
						variants={{
							visible: { opacity: 1 },
							hidden: { opacity: 0 },
						}}>
						<div
							className={cx(styles.search, {
								[styles.alternate]: type === 'alternate',
								[className]: !!className,
							})}>
							{type !== 'compact' && (
								<p
									className={cx(styles.copy, {
										[styles.dark]: theme === 'dark',
									})}>
									{type === 'standard'
										? 'Product search'
										: 'Search for:'}
								</p>
							)}

							<div className={styles.searchBounds}>
								<div
									className={cx(styles.searchArea, {
										[styles.active]: focus,
										[styles.dark]: theme === 'dark',
									})}>
									<form
										action="/search-results"
										method="get"
										autoComplete="off">
										<div className={styles.bounds}>
											<input
												type="text"
												value={searchQuery}
												onInput={(e) =>
													setSearchQuery(
														e.target.value
													)
												}
												name="s"
												aria-label="Search"
												className={cx(styles.input, {
													[styles.dark]:
														theme === 'dark',
												})}
												placeholder={
													type === 'compact'
														? 'Search for'
														: ''
												}
											/>
											<button
												type="submit"
												aria-label="Submit search query"
												className={styles.button}>
												<SearchIcon
													className={cx(styles.icon, {
														[styles.dark]:
															theme === 'dark',
													})}
												/>
											</button>
										</div>
									</form>

									{focus && searchQuery && (
										<div
											className={cx(styles.results, {
												[styles.dark]: theme === 'dark',
											})}>
											{queriedResults?.length > 0 ? (
												<ul className={styles.list}>
													{queriedResults.map(
														(item, key) => {
															const {
																title,
																type,
															} = item;

															let slug;

															if (
																type ===
																'product'
															)
																slug =
																	constructProductSlug(
																		item,
																		groups
																	);

															if (
																type ===
																'product_group'
															)
																slug =
																	constructGroupSlug(
																		item,
																		categories
																	);

															if (
																type ===
																'product_category'
															)
																slug = `${LINKS.PRODUCTS}/${item.uid}`;

															return (
																<li key={key}>
																	<Link
																		to={
																			slug
																				? slug
																				: '/'
																		}
																		className={cx(
																			styles.link,
																			{
																				[styles.dark]:
																					theme ===
																					'dark',
																			}
																		)}>
																		{title}
																	</Link>
																</li>
															);
														}
													)}
												</ul>
											) : (
												<Copy
													className={cx(styles.copy, {
														[styles.dark]:
															theme === 'dark',
													})}>
													No product results found
												</Copy>
											)}
										</div>
									)}
								</div>
							</div>
						</div>
					</motion.div>
				);
			}}
		/>
	);
};

export default ProductSearch;
